import { useState, useEffect } from "react";
import { Activity, CheckCircle2, XCircle, ShieldCheck, Tag, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { EVENT_TAXONOMY, getConfigStatus, getStoredConsent } from "@/lib/analytics";

const GROUP_ORDER = ["Core", "Commercial", "Conversion", "Future"];

export function AnalyticsPanel() {
  const [cfg, setCfg] = useState(null);
  const [health, setHealth] = useState(null);
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    // Give gtag a tick to initialize so tag detection is accurate.
    const t = setTimeout(() => setCfg(getConfigStatus()), 400);
    setConsent(getStoredConsent());
    api.analyticsHealth().then((d) => setHealth(d.events)).catch(() => setHealth([]));
    return () => clearTimeout(t);
  }, []);

  const seen = Object.fromEntries((health || []).map((e) => [e.name, e]));
  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : null;
  const conversions = [
    { label: "Advertising Request", on: cfg?.adsLabels?.advertising },
    { label: "Suggestion", on: cfg?.adsLabels?.suggestion },
    { label: "Newsletter", on: cfg?.adsLabels?.newsletter },
  ].filter((c) => c.on);

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      <div className="grid gap-5 md:grid-cols-3">
        {/* GA4 */}
        <Card icon={Activity} title="Google Analytics 4">
          {!cfg ? <Skel /> : (
            <>
              <Status ok={!!cfg.measurementId && cfg.tagDetected} okText="Connected" badText="Configuration Missing" testid="ga4-status" />
              <Row label="Measurement ID" value={cfg.measurementId || "—"} mono testid="ga4-measurement-id" />
              <Row label="Implementation" value="Google tag / gtag.js" />
              <Row label="Environment" value={cfg.environment} testid="ga4-environment" />
              <Row label="Tag" value={cfg.tagDetected ? "Detected" : "Not detected"} good={cfg.tagDetected} testid="ga4-tag-detected" />
              <Row label="Config executed" value={cfg.configExecuted ? "Yes" : "No"} good={cfg.configExecuted} />
            </>
          )}
        </Card>

        {/* Consent Mode */}
        <Card icon={ShieldCheck} title="Consent Mode">
          {!cfg ? <Skel /> : (
            <>
              <Row label="Consent Mode" value="v2" />
              <Row label="Default Analytics" value="Denied" />
              <Row label="Default Advertising" value="Denied" />
              <Row label="Consent UI" value="Configured" good testid="consent-ui-status" />
              <Row label="Mechanism" value="KLYROO Consent Banner" />
              <Row label="This browser's choice"
                value={consent ? `Analytics: ${consent.analytics ? "Granted" : "Denied"} · Ads: ${consent.advertising ? "Granted" : "Denied"}` : "No choice stored yet"}
                testid="consent-current-choice" />
            </>
          )}
        </Card>

        {/* Google Ads */}
        <Card icon={Tag} title="Google Ads">
          {!cfg ? <Skel /> : (
            <>
              <Status ok={!!cfg.adsConversionId} okText="Configured" badText="Not Configured" testid="ads-status" />
              <Row label="Conversion ID"
                value={cfg.adsConversionId ? `AW-••••••••  (configured)` : "Not configured"} mono testid="ads-conversion-id" />
              <div className="mt-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Conversions configured</div>
                {conversions.length ? conversions.map((c) => (
                  <div key={c.label} className="text-sm text-emerald-300 flex items-center gap-1.5"><CheckCircle2 size={13} />{c.label}</div>
                )) : <div className="text-sm text-slate-500">No conversions configured yet</div>}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Event health */}
      <Card icon={Activity} title="Event Health" wide>
        <p className="text-xs text-slate-500 mb-3">GA4 is the source of truth for analytics data. This is a first-party firing check (event name + time only — no parameters, no PII).</p>
        {!health ? <Skel /> : (
          <div className="space-y-4" data-testid="event-health-list">
            {GROUP_ORDER.map((group) => {
              const rows = EVENT_TAXONOMY.filter((e) => e.group === group);
              return (
                <div key={group}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-2">{group}</div>
                  <div className="rounded-xl border border-white/5 divide-y divide-white/5">
                    {rows.map((e) => {
                      const h = seen[e.name];
                      return (
                        <div key={e.name} className="flex items-center gap-3 px-4 py-2.5" data-testid={`event-row-${e.name}`}>
                          <code className="text-sm text-white flex-1 min-w-0 truncate">{e.name}</code>
                          {e.bound
                            ? <span className="text-[11px] inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={12} /> Implemented</span>
                            : <span className="text-[11px] inline-flex items-center gap-1 text-slate-500"><XCircle size={12} /> Not wired</span>}
                          <span className="text-xs text-slate-400 w-44 text-right hidden sm:block" data-testid={`event-lastseen-${e.name}`}>
                            {h && h.last_seen ? fmt(h.last_seen) : "No event observed yet"}
                          </span>
                          <span className="text-[11px] font-mono text-slate-500 w-16 text-right">{h ? `${h.count}×` : "0×"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex items-start gap-2 text-xs text-slate-500 px-1">
        <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
        <span>This is a technical status view. It does not by itself make KLYROO legally compliant — privacy/cookie obligations depend on the jurisdictions KLYROO operates in and its actual data use.</span>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children, wide }) {
  return (
    <div className={`card-surface rounded-2xl p-5 ${wide ? "md:col-span-3" : ""}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-klyroo-cyan/10 flex items-center justify-center text-klyroo-cyan"><Icon size={16} /></span>
        <h3 className="font-display font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}
function Row({ label, value, mono, good, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm text-right ${good ? "text-emerald-300" : "text-slate-200"} ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</span>
    </div>
  );
}
function Status({ ok, okText, badText, testid }) {
  return (
    <div className={`mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-semibold ${ok ? "bg-emerald-400/10 text-emerald-300" : "bg-amber-400/10 text-amber-300"}`} data-testid={testid}>
      {ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{ok ? okText : badText}
    </div>
  );
}
function Skel() {
  return <div className="py-6 text-center text-slate-500"><Loader2 className="animate-spin mx-auto" size={18} /></div>;
}
